import React, { useId, useMemo, useState } from 'react';
import './LocationSelect.css';
import { useOffline } from '../context/OfflineContext';
import { isAdmin } from '../../util/auth';
import LazyLocationMapDialog from './LazyLocationMapDialog';

const NEW_OPTION = '__new';

/**
 * Location picker for the event forms: controlled native <select> with "Neue Location …", which opens the
 * map dialog. Deliberately not CustomDropdown — that keeps its own value state and would stay on
 * "Neue Location …" after the dialog is cancelled. Creating is admin-only and disabled offline; editing
 * and deleting existing locations lives on the admin page /locations (components/pages/Locations).
 *
 * Props:
 *  - locations, loading       from useLocations()
 *  - value                    selected location id (number) or null
 *  - onChange(id)             id is a number or null
 *  - onLocationSaved(loc)     called after the dialog saved (parent upserts into its list)
 *  - currentLocation          optional location object kept selectable even if the list failed to load
 *  - required, disabled
 *  - label                    floating label text; falsy = no label (popup sections use an <h3>)
 */
function LocationSelect({
  locations = [],
  loading = false,
  value = null,
  onChange,
  onLocationSaved,
  currentLocation = null,
  required = false,
  disabled = false,
  label = 'Location',
}) {
  const { isOffline } = useOffline();
  const canCreate = isAdmin();
  const selectId = useId();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEverOpened, setDialogEverOpened] = useState(false);

  const options = useMemo(() => {
    if (currentLocation?.id != null && !locations.some(l => l.id === currentLocation.id)) {
      return [...locations, currentLocation];
    }
    return locations;
  }, [locations, currentLocation]);

  const selected = value == null ? null : (options.find(l => l.id === Number(value)) ?? null);

  const closeDialog = () => setDialogOpen(false);

  const handleChange = (e) => {
    const next = e.target.value;
    if (next === NEW_OPTION) {
      // Not passed to onChange: the controlled select snaps back to `value` until the dialog saved.
      if (canCreate && !isOffline) {
        setDialogOpen(true);
        setDialogEverOpened(true);
      }
      return;
    }
    onChange?.(next === '' ? null : Number(next));
  };

  const handleSaved = (location) => {
    onLocationSaved?.(location);
    if (location?.id != null) onChange?.(location.id);
    closeDialog();
  };

  const placeholder = loading && options.length === 0 ? 'Lädt …' : '– Location wählen –';

  return (
    <div className={`formFieldWrapper locationSelect${label ? '' : ' noLabel'}`}>
      {label && <label className="formFieldLabel" htmlFor={selectId}>{label}</label>}
      <div className="locationSelectInputWrap">
        <select
          id={selectId}
          className={`locationSelectInput${selected ? '' : ' isEmpty'}`}
          value={value == null ? '' : String(value)}
          onChange={handleChange}
          required={required}
          disabled={disabled}
          title={selected?.address || undefined}
        >
          <option value="" disabled>{placeholder}</option>
          {options.map(l => (
            <option key={l.id} value={String(l.id)}>{l.name}</option>
          ))}
          {canCreate && (
            <option value={NEW_OPTION} disabled={isOffline}>
              {isOffline ? '+ Neue Location … (offline nicht verfügbar)' : '+ Neue Location …'}
            </option>
          )}
        </select>
        <span className="material-symbols-rounded locationSelectArrow" aria-hidden="true">expand_more</span>
      </div>
      {dialogEverOpened && (
        <LazyLocationMapDialog
          open={dialogOpen}
          mode="create"
          initial={null}
          fallbackCenter={selected ?? options[0] ?? null}
          existingLocations={options}
          onClose={closeDialog}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

export default LocationSelect;
